import sourceMap from 'source-map-js'
import * as StackTraceParser from 'stacktrace-parser'

type SourceMapConsumer = InstanceType<typeof sourceMap.SourceMapConsumer>
type RawSourceMap = ConstructorParameters<typeof sourceMap.SourceMapConsumer>[0]

type ResolvedFrame = {
	file: string | null
	methodName: string | null
	lineNumber: number | null
	column: number | null
}

const consumerCache = new Map<string, SourceMapConsumer | null>()
const consumerPromises = new Map<string, Promise<SourceMapConsumer | null>>()

function loadConsumer(jsUrl: string): Promise<SourceMapConsumer | null> {
	if (consumerCache.has(jsUrl)) {
		return Promise.resolve(consumerCache.get(jsUrl) ?? null)
	}
	const existing = consumerPromises.get(jsUrl)
	if (existing) return existing

	const promise = (async () => {
		try {
			const res = await fetch(jsUrl + '.map')
			if (!res.ok) throw new Error(`map fetch ${res.status}`)
			const raw = (await res.json()) as RawSourceMap
			const consumer = new sourceMap.SourceMapConsumer(raw)
			consumerCache.set(jsUrl, consumer)
			return consumer
		} catch {
			consumerCache.set(jsUrl, null)
			return null
		}
	})()
	consumerPromises.set(jsUrl, promise)
	return promise
}

async function resolveFrame(frame: StackTraceParser.StackFrame): Promise<ResolvedFrame> {
	const fallback: ResolvedFrame = {
		file: frame.file,
		methodName: frame.methodName || null,
		lineNumber: frame.lineNumber,
		column: frame.column,
	}
	if (!frame.file || !frame.lineNumber) return fallback
	const consumer = await loadConsumer(frame.file)
	if (!consumer) return fallback
	const orig = consumer.originalPositionFor({
		line: frame.lineNumber,
		column: frame.column ?? 0,
	})
	if (!orig.source) return fallback
	return {
		file: orig.source,
		methodName: orig.name || frame.methodName || null,
		lineNumber: orig.line ?? null,
		column: orig.column ?? null,
	}
}

function formatFrame(f: ResolvedFrame): string {
	const name = f.methodName || '<anonymous>'
	const file = f.file || '?'
	const line = f.lineNumber ?? '?'
	const col = f.column ?? '?'
	return `    at ${name} (${file}:${line}:${col})`
}

type ReportInput = {
	name?: string
	message?: string
	stack?: string
	filename?: string
	lineno?: number
	colno?: number
}

export async function reportError(input: ReportInput | Error, meta?: Record<string, unknown>): Promise<void> {
	const swetrix = (window as any).swetrix
	if (!swetrix || typeof swetrix.trackError !== 'function') return

	const name = input.name || 'Error'
	const message = input.message || ''
	const stack = (input as any).stack as string | undefined

	let frames: StackTraceParser.StackFrame[] = []
	if (stack) {
		try { frames = StackTraceParser.parse(stack) } catch { frames = [] }
	}

	let resolved: ResolvedFrame[]
	if (frames.length > 0) {
		resolved = await Promise.all(frames.map(resolveFrame))
	} else if ((input as ReportInput).filename) {
		const i = input as ReportInput
		resolved = [await resolveFrame({
			file: i.filename ?? null,
			methodName: null,
			lineNumber: i.lineno ?? null,
			column: i.colno ?? null,
			arguments: [],
		} as any)]
	} else {
		resolved = []
	}

	const top = resolved[0]
	const stackTrace = resolved.length > 0 ? resolved.map(formatFrame).join('\n') : stack

	try {
		swetrix.trackError({
			name: name.substring(0, 200),
			message: message.substring(0, 300),
			filename: top?.file ?? undefined,
			lineno: top?.lineNumber ?? undefined,
			colno: top?.column ?? undefined,
			stackTrace: stackTrace ? stackTrace.substring(0, 1900) : undefined,
			meta,
		})
	} catch {
		// swallow — never let error reporting throw
	}
}

export function installGlobalErrorHandlers(): void {
	window.addEventListener('error', (e: ErrorEvent) => {
		if (e.error instanceof Error) {
			void reportError(e.error)
		} else {
			void reportError({
				name: 'Error',
				message: e.message,
				filename: e.filename,
				lineno: e.lineno,
				colno: e.colno,
			})
		}
	})

	window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
		const reason = e.reason
		if (reason instanceof Error) {
			void reportError(reason)
		} else {
			void reportError({
				name: 'UnhandledRejection',
				message: typeof reason === 'string' ? reason : JSON.stringify(reason),
			})
		}
	})
}
